import { InfoIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Card,
  HStack,
  Icon,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { ExclamationIcon } from '@heroicons/react/outline';
import { SingleValue } from 'chakra-react-select';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  FiscalAccountInfosFragment,
  FiscalAccountType,
  FundraisingStatus,
  HoldingMethod,
  IdentityType,
  useCreateFiscalAccountMutation,
  useGetFiscalAccountsQuery,
  useGetSubscriptionQuery,
  useUpdateSubscriptionHoldingMethodMutation,
} from '@blockpulse3/graphql/hooks';
import { ErrorQueryCard, useErrorToast, useSuccessToast } from '@blockpulse3/ui/commons';
import { FiscalAccountFormModal, IFiscalAccountForm } from '@blockpulse3/web-client/auth';
import { useUser } from '@blockpulse3/web-client/auth';

import { checkFiscalAccountHoldingMethod } from '../../../utils';
import { HoldingMethodOption, HoldingMethodSelect } from '../../HoldingMethodSelect';

/* ** Translation casts ** */
type HoldingMethodTypeMessage = keyof Messages['HoldingMethodTypeValue'];

/**
 * SubscriptionHoldingMethod.
 * Does not render if subscription is a company.
 *
 * @param {Props}
 * @returns {JSX.Element | null}
 */
export function SubscriptionHoldingMethod(): JSX.Element | null {
  const i18nFiscalAccountForm = useTranslations('FiscalAccount');
  const i18nHoldingMethodTypeValue = useTranslations('HoldingMethodTypeValue');
  const i18nSubscriptionHoldingMethod = useTranslations('SubscriptionHoldingMethod');

  const formatFiscalAccount = (fiscalAccount: FiscalAccountInfosFragment): HoldingMethodOption => {
    return {
      label: `${i18nHoldingMethodTypeValue(fiscalAccount.type as HoldingMethodTypeMessage)} ${
        fiscalAccount.accountNumber
      } ${fiscalAccount.bankName}`,
      value: fiscalAccount.id,
      type: HoldingMethod[fiscalAccount.type],
    };
  };

  const formatHoldingMethod = (holdingMethod: HoldingMethod): HoldingMethodOption => {
    return {
      label: `${i18nHoldingMethodTypeValue(holdingMethod as HoldingMethodTypeMessage)}`,
      type: holdingMethod,
      value: null,
    };
  };

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const formModal = useDisclosure();

  const [selectedHoldingMethod, setSelectedHoldingMethod] = useState<HoldingMethodOption>();

  const { user } = useUser();
  const { subscriptionId = '' } = useParams();
  const subscriptionReq = useGetSubscriptionQuery({
    variables: { subscriptionId },
    onCompleted: (data) => {
      let holdingMethod: HoldingMethodOption;
      if (data.subscription.fiscalAccount) {
        holdingMethod = formatFiscalAccount(data.subscription.fiscalAccount);
      } else {
        holdingMethod = formatHoldingMethod(data.subscription.holdingMethod);
      }
      setSelectedHoldingMethod(holdingMethod);
    },
  });

  const fiscalAccountReq = useGetFiscalAccountsQuery({
    variables: { userId: user?.id || '' },
    skip: !user?.id,
  });
  const [createFiscalAccount] = useCreateFiscalAccountMutation();
  const [updateSubscriptionHoldingMethod] = useUpdateSubscriptionHoldingMethodMutation();

  if (subscriptionReq.loading) {
    return (
      <Skeleton>
        <Card h="200px" />
      </Skeleton>
    );
  }

  if (subscriptionReq.error) {
    return <ErrorQueryCard />;
  }

  if (!subscriptionReq.data) {
    return <ErrorQueryCard />;
  }

  const { subscription } = subscriptionReq.data;
  const { fiscalAccounts = [] } = fiscalAccountReq.data || {};

  /* ** Allowed holding methods for filtering ** */
  const allowedHoldingMethods = subscription.fundraising.allowedHoldingMethods;

  /* ** For tooltip conditions ** */
  const isFundraisingClosed = [FundraisingStatus.FINALIZED, FundraisingStatus.CLOSED].includes(
    subscription?.fundraising.status,
  );

  /* ** Do not render if subscription is a company ** */
  if (subscription.identity.type !== IdentityType.INDIVIDUAL) {
    return null;
  }

  /* ** Format holding methods and fiscal accounts into select options ** */
  const getHoldingMethodOptions = (): HoldingMethodOption[] => {
    /* ** Return default holding method option if none are allowed by fundraising ** */
    let defaultHoldingMethod = HoldingMethod.DIRECT;
    if (!allowedHoldingMethods) return [formatHoldingMethod(HoldingMethod.DIRECT)];

    /* ** Replace default holding method if allowed by fundraising, for now only IR_PME is a known default method ** */
    if (allowedHoldingMethods.includes(HoldingMethod.IR_PME)) {
      defaultHoldingMethod = HoldingMethod.IR_PME;
    }

    const holdingMethodsTypeArray = Object.values(HoldingMethod);
    const fiscalAccountsTypeArray = Object.keys(FiscalAccountType);
    /* ** Get default holding methods by removing fiscal account types and not allowed holding methods from the array ** */
    const formattedHoldingMethods = holdingMethodsTypeArray
      .filter((holdingMethod) => {
        const isDefault = holdingMethod === defaultHoldingMethod;
        const isFiscalAccount = fiscalAccountsTypeArray.includes(holdingMethod);
        const isAllowed = allowedHoldingMethods.includes(holdingMethod);
        return isDefault || (!isFiscalAccount && isAllowed);
      })
      .map(formatHoldingMethod);

    /* ** Fill the array with created fiscal accounts if any ** */
    const formattedFiscalAccounts = fiscalAccounts
      .filter((fiscalAccount) => allowedHoldingMethods.includes(HoldingMethod[fiscalAccount.type]))
      .map(formatFiscalAccount);

    return [...formattedHoldingMethods, ...formattedFiscalAccounts];
  };

  const options = getHoldingMethodOptions();

  const canAddFiscalAccount = checkFiscalAccountHoldingMethod(allowedHoldingMethods);
  const isSelectDisabled = (options.length === 1 && !canAddFiscalAccount) || isFundraisingClosed;

  const handleHoldingMethodChange = (option: SingleValue<HoldingMethodOption>): void => {
    /* ** Prevent unecessary holding method update ** */
    const hasMethodChanged = JSON.stringify(option) === JSON.stringify(selectedHoldingMethod);

    if (option && !hasMethodChanged) {
      updateSubscriptionHoldingMethod({
        variables: {
          updateSubscriptionHoldingMethodInput: {
            subscriptionId: subscription.id,
            holdingMethod: option.type,
            fiscalAccountId: option.value,
          },
        },
        onCompleted: () => {
          setSelectedHoldingMethod(option);
        },
        onError: () => {
          errorToast({ title: i18nSubscriptionHoldingMethod('Toast.AttachError') });
        },
      });
    }
  };

  const handleFiscalAccountAdd = (): void => {
    formModal.onOpen();
  };

  const handleFiscalAccountCreate = async (data: IFiscalAccountForm): Promise<void> => {
    if (user?.id) {
      createFiscalAccount({
        variables: {
          createFiscalAccountInput: {
            userId: user.id,
            accountNumber: data.accountNumber,
            type: data.type.value,
            bankName: data.bankName,
            bankCode: data.bankCode,
            bankEmail: data.bankEmail || null,
          },
        },
        onCompleted: (data) => {
          const option = data.createFiscalAccount;
          if (!allowedHoldingMethods?.includes(HoldingMethod[option.type])) return;

          updateSubscriptionHoldingMethod({
            variables: {
              updateSubscriptionHoldingMethodInput: {
                fiscalAccountId: option.id,
                subscriptionId: subscription.id,
                holdingMethod: HoldingMethod[option.type],
              },
            },
            onCompleted: () => {
              successToast({ title: i18nSubscriptionHoldingMethod('Toast.AttachSuccess') });
              setSelectedHoldingMethod(formatFiscalAccount(option));
              fiscalAccountReq.refetch();
            },
            onError: () => {
              errorToast({ title: i18nSubscriptionHoldingMethod('Toast.AttachError') });
            },
          });
          formModal.onClose();
        },
        onError: () => {
          formModal.onClose();
          errorToast({ title: i18nFiscalAccountForm('Toast.CreateError') });
        },
      });
    }
    formModal.onClose();
  };

  return (
    <>
      <HStack>
        <Text fontSize="lg" fontWeight="600">
          {i18nSubscriptionHoldingMethod('HoldingMethodSelection')}
        </Text>
        <Tooltip
          hasArrow
          label={i18nSubscriptionHoldingMethod('Tooltip.HoldingMethodDescription')}
          placement="top"
        >
          <Icon as={InfoIcon} color="gray.600" />
        </Tooltip>
      </HStack>
      <Stack spacing="2">
        <Box maxWidth={{ base: 'full', md: '300px' }}>
          <HoldingMethodSelect
            allowedHoldingMethods={allowedHoldingMethods}
            isDisabled={isSelectDisabled}
            isLoading={fiscalAccountReq.loading}
            options={options}
            value={selectedHoldingMethod}
            onAddFiscalAccount={handleFiscalAccountAdd}
            onChange={handleHoldingMethodChange}
          />
        </Box>
        {canAddFiscalAccount && (
          <Alert status="warning">
            <AlertIcon as={ExclamationIcon} />
            <AlertTitle>{i18nSubscriptionHoldingMethod('WarningCard.Description')}</AlertTitle>
          </Alert>
        )}
      </Stack>
      <FiscalAccountFormModal
        allowedHoldingMethods={allowedHoldingMethods}
        isOpen={formModal.isOpen}
        onClose={formModal.onClose}
        onSubmit={handleFiscalAccountCreate}
      />
    </>
  );
}
